import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, MapPin, Clock, Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import AdvertisingBanner from "@/components/AdvertisingBanner";
import { useOnboarding } from "@/hooks/useOnboarding";

const getOnboardingScreens = (t: (key: string) => string) => [
  {
    icon: Clock,
    title: t('screen1Title'),
    description: t('screen1Desc')
  },
  {
    icon: MapPin,
    title: t('screen2Title'),
    description: t('screen2Desc')
  },
  {
    icon: Users,
    title: t('screen3Title'),
    description: t('screen3Desc')
  }
];

const Onboarding = () => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { completeOnboarding } = useOnboarding();
  
  const onboardingScreens = getOnboardingScreens(t);

  const nextScreen = async () => {
    if (currentScreen < onboardingScreens.length - 1) {
      setCurrentScreen(currentScreen + 1);
    } else {
      await completeOnboarding();
      navigate("/");
    }
  };

  const prevScreen = () => {
    if (currentScreen > 0) {
      setCurrentScreen(currentScreen - 1);
    }
  };

  const screen = onboardingScreens[currentScreen];
  const IconComponent = screen.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/20 via-background to-secondary/20 flex flex-col">
      {/* Language Toggle */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageToggle />
      </div>

      {/* Advertising Banner */}
      <div className="px-4 pt-4">
        <AdvertisingBanner />
      </div>

      {/* Header */}
      <div className="text-center pt-8 pb-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-primary rounded-full flex items-center justify-center">
          <MapPin className="w-8 h-8 text-primary-foreground" />
        </div>
        <h1 className="text-3xl font-bold text-primary mb-2">{t('appTitle')}</h1>
        <p className="text-muted-foreground">{t('tagline')}</p>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <Card className="w-full max-w-sm">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
              <IconComponent className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-4">{screen.title}</h2>
            <p className="text-muted-foreground mb-8">{screen.description}</p>
            
            {/* Progress dots */}
            <div className="flex justify-center gap-2 mb-8">
              {onboardingScreens.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentScreen ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <div className="p-6 flex justify-between items-center">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="text-muted-foreground"
        >
          {t('skip')}
        </Button>

        <Button
          variant="ghost"
          onClick={prevScreen}
          disabled={currentScreen === 0}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('back')}
        </Button>

        <Button onClick={nextScreen} className="flex items-center gap-2">
          {currentScreen === onboardingScreens.length - 1 ? t('letsGo') : t('next')}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default Onboarding;